import { useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import type { DocHandle } from "../sync/docManager";
import { useNow } from "../core/useNow";
import { useDocSelector } from "../core/useDocSelector";
import { readTask } from "../core/tasks";
import { advanceIfDue, phaseEndMs, readPomodoro } from "../tracking/pomodoro";
import { readCurrent, stopTracking } from "../tracking/timelog";
import { applyAutoPause } from "../tracking/linked";
import { dueReminders, listReminders, markFired } from "../reminders/reminders";
import { notify } from "./notify";
import type { Settings } from "./settings";

const PRE_WARNING_MS = 2 * 60_000;

/**
 * Device-side runners (no UI): pomodoro transitions with a 2-minute pre-warning (§9.19 transition
 * warnings), reminders (§9.13), break reminders and the idle prompt (§9.6). Everything is a dismissable
 * toast/notification; nothing is modal and nothing navigates.
 */
export function Background({
  core,
  settings,
  kiosk,
  me,
  workspaceId,
}: {
  core: DocHandle | null;
  workspaceId?: string | null;
  settings: Settings;
  kiosk: boolean;
  /** in a shared space: reminders of other profiles are not mine to fire (spec §9.29) */
  me?: string | null;
}) {
  const { t } = useTranslation();
  const now = useNow();
  const pomodoro = useDocSelector(core, (doc) => readPomodoro(doc));
  const current = useDocSelector(core, (doc) => readCurrent(doc));
  const reminders = useDocSelector(core, (doc) => listReminders(doc));
  const warned = useRef<string | null>(null);
  const breakAt = useRef<string | null>(null);
  const lastInput = useRef(0);
  const idlePrompted = useRef<string | null>(null);
  const quiet = kiosk; // kiosks never notify (spec §9.13)

  // pomodoro: pre-warning, then the transition (written by whichever device sees it first)
  useEffect(() => {
    if (!core || core.readOnly || !pomodoro || pomodoro.pausedAt) return;
    const end = phaseEndMs(pomodoro, settings.pomodoro);
    const key = `${pomodoro.phase}:${pomodoro.phaseStartedAt}`;
    if (!quiet && end - now <= PRE_WARNING_MS && end - now > 0 && warned.current !== key) {
      warned.current = key;
      notify(t("pomodoro.preWarning", { phase: t(`pomodoro.phase.${pomodoro.phase}`) }), { tag: "pomodoro" });
    }
    if (now >= end) {
      const next = advanceIfDue(core.doc, settings.pomodoro, now);
      if (next && !quiet)
        notify(t("pomodoro.phaseStarted", { phase: t(`pomodoro.phase.${next.phase}`) }), { tag: "pomodoro" });
    }
  }, [core, pomodoro, now, settings.pomodoro, quiet, t]);

  // auto-pause the task timer during Pomodoro breaks (Settings → Pomodoro); only a break's own pause resumes
  useEffect(() => {
    if (core && !core.readOnly && workspaceId && settings.pomodoro.autoPauseInBreaks)
      void applyAutoPause(core.doc, workspaceId, now);
  }, [core, workspaceId, now, settings.pomodoro.autoPauseInBreaks]);

  // reminders
  useEffect(() => {
    if (!core || core.readOnly || !reminders?.length) return;
    // read from the doc, not from `reminders`: a hidden tab gets no animation frames, so the selected list
    // keeps its old `firedAt` and the same reminder fired again on every tick (bug hunt M28)
    for (const r of dueReminders(listReminders(core.doc), now, me)) {
      markFired(core.doc, r, new Date(now));
      if (!quiet)
        notify(t("reminders.fired", { title: readTask(core.doc, r.taskId)?.title ?? "" }), {
          tag: `reminder-${r.id}`,
          ms: 30_000,
        });
    }
  }, [core, reminders, now, quiet, t, me]);

  // break reminder: after `everyMin` of continuous tracking
  useEffect(() => {
    if (!core || quiet || !current || current.pausedAt || !settings.breaks.everyMin) return;
    const elapsed = now - Date.parse(current.startedAt);
    const n = Math.floor(elapsed / (settings.breaks.everyMin * 60_000));
    const key = `${current.entryId}:${n}`;
    if (n >= 1 && breakAt.current !== key) {
      breakAt.current = key;
      notify(t("breaks.reminder", { n: settings.breaks.lengthMin }), { tag: "break" });
    }
  }, [core, current, now, settings.breaks.everyMin, settings.breaks.lengthMin, quiet, t]);

  // idle heuristic (browser only, §9.6): no input for N minutes while a timer runs → one prompt
  useEffect(() => {
    const touch = () => (lastInput.current = Date.now());
    touch();
    const events = ["pointerdown", "keydown", "pointermove", "visibilitychange"] as const;
    for (const e of events) window.addEventListener(e, touch, { passive: true });
    return () => {
      for (const e of events) window.removeEventListener(e, touch);
    };
  }, []);
  useEffect(() => {
    if (!core || quiet || !current || current.pausedAt || !settings.idle.promptAfterMin) return;
    const idleFor = now - lastInput.current;
    if (idleFor >= settings.idle.promptAfterMin * 60_000 && idlePrompted.current !== current.entryId) {
      idlePrompted.current = current.entryId;
      const since = new Date(lastInput.current);
      notify(t("idle.prompt", { n: settings.idle.promptAfterMin }), {
        ms: 60_000,
        tag: "idle",
        action: {
          label: t("idle.stopThen", {
            time: since.toLocaleTimeString(undefined, { hour: "2-digit", minute: "2-digit" }),
          }),
          run: () => void stopTracking(core.doc, since),
        },
      });
    }
    if (idleFor < 5000 && idlePrompted.current === current.entryId) idlePrompted.current = null;
  }, [core, current, now, settings.idle.promptAfterMin, quiet, t]);

  return null;
}
